import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  CircularProgress,
  Tooltip,
  Chip,
} from "@mui/material";
import dayjs from "dayjs";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DoneIcon from "@mui/icons-material/Done";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { setArchivesPage } from "../../store/slices/bondArchivesSlice";
import TablePaginationActions from "../../globals/components/TablePaginationActions";

const BondArchivesTable = ({
  tableColumns,
  orderBy,
  order,
  handleSort,
  loading,
  filteredRows,
  filteredData,
  page,
  setPage,
  dispatch,
  rowsPerPage,
  setRowsPerPage,
  isMarkingLibertyComplete,
  handleViewBond,
  handleLibertyIconClick,
  handleCancelIconClick,
  handleEndorseIconClick,
  handleDeleteClick,
  role,
  onPaymentStatusOverrideClick,
  getPaymentStatusChipProps,
}) => {
  const dataColCount = tableColumns.length;
  const totalColSpan = dataColCount + 1;

  function renderTableBody() {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={totalColSpan} align="center">
            <CircularProgress size={28} />
          </TableCell>
        </TableRow>
      );
    }
    if (filteredRows.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={totalColSpan} align="center">
            No Data Available
          </TableCell>
        </TableRow>
      );
    }
    return filteredRows.map((bond, index) => (
      <TableRow
        key={
          bond.id ??
          bond.pk ??
          bond.bond_number ??
          `archive-bond-${bond.created_at ?? ""}-${index}`
        }
      >
        <TableCell>{bond.bond_number}</TableCell>
        <TableCell>{bond.bond_type}</TableCell>
        <TableCell>{bond.bond_sub_category}</TableCell>
        <TableCell>{Array.isArray(bond.principals) ? bond.principals.join(", ") : (bond.principals || "N/A")}</TableCell>
        <TableCell>{bond.estate_name}</TableCell>
        <TableCell>{bond.law_firm_name}</TableCell>
        <TableCell>{bond.attorney_name}</TableCell>
        <TableCell>{Array.isArray(bond.managed_by) ? bond.managed_by.join(", ") : (bond.managed_by || "N/A")}</TableCell>
        <TableCell>
          ${Number(bond.bond_amount).toLocaleString()}
        </TableCell>
        <TableCell>{bond.status}</TableCell>
        <TableCell>
          {dayjs(bond.created_at).format("DD MMM YYYY")}
        </TableCell>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
            <Chip size="small" {...getPaymentStatusChipProps(bond)} />
            {(role === "internal_user" || role === "super_user") && (
              <Tooltip title="Override payment status">
                <IconButton
                  size="small"
                  aria-label="Override payment status"
                  onClick={() => onPaymentStatusOverrideClick(bond)}
                >
                  <EditNoteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell align="center">
          <Tooltip title="View Bond">
            <IconButton
              onClick={() => handleViewBond(bond)}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Mark Liberty Complete">
            <IconButton
              color="success"
              onClick={() => handleLibertyIconClick(bond)}
              disabled={isMarkingLibertyComplete}
            >
              <DoneIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel Bond">
            <IconButton
              color="error"
              onClick={() => handleCancelIconClick(bond)}
            >
              <CancelIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Bond Endorsement">
            <IconButton
              color="secondary"
              onClick={() => handleEndorseIconClick(bond)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Bond">
            <IconButton
              color="error"
              onClick={() => handleDeleteClick(bond)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    ));
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
              {tableColumns.map((col) => (
                <TableCell
                  key={col}
                  sortDirection={orderBy === col ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === col}
                    direction={orderBy === col ? order : "asc"}
                    onClick={() => handleSort(col)}
                  >
                    {col
                      .replaceAll("_", " ")
                      .replace(/\b\w/g, (c) => c.toUpperCase())}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ "& tr:nth-of-type(odd)": { backgroundColor: "#fafafa" } }}>
            {renderTableBody()}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        ActionsComponent={TablePaginationActions}
        component="div"
        count={filteredData.length}
        page={page}
        onPageChange={(e, p) => {
          setPage(p);
          dispatch(setArchivesPage(p));
        }}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => setRowsPerPage(Number.parseInt(e.target.value, 10))}
        rowsPerPageOptions={[5, 10, 25]}
        sx={{ mt: 2 }}
      />
    </>
  );
};

BondArchivesTable.propTypes = {
  tableColumns: PropTypes.array.isRequired,
  orderBy: PropTypes.string,
  order: PropTypes.string,
  handleSort: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  filteredRows: PropTypes.array.isRequired,
  filteredData: PropTypes.array.isRequired,
  page: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  setRowsPerPage: PropTypes.func.isRequired,
  isMarkingLibertyComplete: PropTypes.bool,
  handleViewBond: PropTypes.func.isRequired,
  handleLibertyIconClick: PropTypes.func.isRequired,
  handleCancelIconClick: PropTypes.func.isRequired,
  handleEndorseIconClick: PropTypes.func.isRequired,
  handleDeleteClick: PropTypes.func.isRequired,
  role: PropTypes.string,
  onPaymentStatusOverrideClick: PropTypes.func,
  getPaymentStatusChipProps: PropTypes.func,
};

export default BondArchivesTable;
